import { effects, reactive, system } from "kho";
import type { Object3D } from "three";
import { $cloudScenes } from "../data/cloud";
import type { Vec3 } from "../data/model";
import { $camera, $renderer } from "../data/scene";
import { $surfacePick, $surfaceSampler } from "../data/surface";
import { createSurfaceSampler } from "../factory/createSurfaceSampler";
import { rayFromNdc } from "../functions/surface";

/**
 * surfaceSystem — owns the point-cloud surface sampler as kho state/behavior.
 *
 * Reads $renderer: when a renderer is available it creates the live GPU
 * SurfaceSampler and publishes it in $surfaceSampler (disposing + recreating on
 * a renderer change, disposing on system dispose). gizmoSystem reads that shared
 * instance instead of newing its own.
 *
 * Sets $surfacePick: the screen-ray point-cloud pick the imperative Viewer calls
 * for drag&drop placement — a coarse CPU raycast against the streamed-tile Points
 * (from $cloudScenes) for the anchor, then (when refine) the GPU sampler refines
 * position + normal via PCA. This is the logic that used to live in
 * viewer.getPointCloudSurface. Positions are point-cloud world coords.
 */

// coarse Points raycast threshold (world units)
const PICK_THRESHOLD = 0.8;
// PCA neighborhood radius (world units)
const SAMPLE_RADIUS = 3;
// ~ streamer gridSize, for the splat px size
const AVG_SPACING = 0.5;

export const surfaceSystem = system((scope) => {
  const { atoms } = scope(reactive);
  const { effect, onDispose } = scope(effects);

  // renderer → live sampler instance (dispose + recreate on renderer change)
  effect([$renderer], () => {
    atoms.get($surfaceSampler)?.dispose();
    const renderer = atoms.get($renderer);
    atoms.set($surfaceSampler, renderer ? createSurfaceSampler(renderer) : null);
  });

  // publish the point-cloud pick used by the Viewer's drag&drop wrappers
  atoms.set($surfacePick, (ndcX, ndcY, refine) => {
    const camera = atoms.get($camera);
    const scenes = atoms.get($cloudScenes);
    if (!camera || scenes.length === 0) return null;

    const points: Object3D[] = [];
    for (const s of scenes)
      s.traverse((o) => {
        if ((o as { isPoints?: boolean }).isPoints) points.push(o);
      });
    if (points.length === 0) return null;

    // coarse hit: nearest tile point along the ray (CPU raycast, no GPU stall)
    const raycaster = rayFromNdc(camera, ndcX, ndcY);
    const prev = raycaster.params.Points?.threshold ?? 1;
    raycaster.params.Points = { threshold: PICK_THRESHOLD };
    const hits = raycaster.intersectObjects(points, false);
    raycaster.params.Points = { threshold: prev };
    if (hits.length === 0) return null;
    const anchor = hits[0].point;
    const up: Vec3 = [0, 0, 1];

    // Drag: coarse position is enough (no per-frame GPU readback → protect FPS).
    // __DESIGN_NO_GPU_SAMPLE__ (self-test / GPU-less env) also uses the coarse hit.
    const noGpu =
      typeof window !== "undefined" &&
      (window as unknown as { __DESIGN_NO_GPU_SAMPLE__?: boolean })
        .__DESIGN_NO_GPU_SAMPLE__ === true;
    if (!refine || noGpu)
      return { position: anchor.toArray() as Vec3, normal: up };

    const sampler = atoms.get($surfaceSampler);
    if (!sampler) return { position: anchor.toArray() as Vec3, normal: up };
    const sample = sampler.sample(
      anchor,
      raycaster.ray.direction,
      SAMPLE_RADIUS,
      scenes,
      AVG_SPACING,
    );
    if (!sample) return { position: anchor.toArray() as Vec3, normal: up };
    return {
      position: sample.centroid.toArray() as Vec3,
      normal: sample.normal.toArray() as Vec3,
    };
  });

  onDispose(() => {
    atoms.get($surfaceSampler)?.dispose();
    atoms.set($surfaceSampler, null);
    atoms.set($surfacePick, null);
  });
});
